import {css} from 'aphrodite';
import classNames from 'classnames';
import gql from 'graphql-tag';
import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DraggableNameTemplate from 'name-templates/DraggableNameTemplate';
import {
  nameTemplateModalStyles,
  NameTemplatePart,
  NameTemplatePartType,
  NameTemplateType,
  nameTemplateTypeToFriendlyName,
} from 'name-templates/NameTemplateConstants';
import {
  getSupportedNameTemplatePartOptions,
  makeExampleForNameTemplate,
} from 'name-templates/NameTemplateUtil';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getNonArchivedOrComputedFields} from 'schemas/fields/SchemaFieldGetters';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {InputChangeEvent} from 'util/Types';

import {
  ComplexPolymerFieldsQuery,
  ComplexPolymerFieldsQueryVariables,
} from './__generated/ComplexPolymerFieldsQuery';
import NameTemplatePartConfiguration from './NameTemplatePartConfiguration';

export interface SetNameTemplateModalProps {
  // expected to be a ModelTypeConstants value or an empty string if there's no relevant entity type
  bioentityType: string;
  schemaFields: Array<SchemaField | ClientSchemaField>;
  schemaId: string | null;
  sequenceType: SequenceSchemaType | null;
  initialNameTemplateParts: Array<NameTemplatePart>;
  initialShouldOrderNamePartsBySequence?: boolean; // undefined if checkbox should be hidden
  onRequestSubmit: (submitArgs: {
    nameTemplateParts: Array<NameTemplatePart>;
    shouldOrderNamePartsBySequence?: boolean;
  }) => void;
  onRequestClose: () => void;
  nameTemplateType: NameTemplateType;
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration;
}

export interface PresentationalSetNameTemplateModalProps extends SetNameTemplateModalProps {
  complexPolymerSchemaFields: Array<SchemaField | ClientSchemaField>;
}

interface State {
  nameTemplateParts: Array<NameTemplatePart>;
  shouldOrderNamePartsBySequence?: boolean; // undefined if checkbox should be hidden
  draggingNameTemplatePartIndex: number;
}

export const SET_NAME_TEMPLATE_MODAL_KEY = 'set-name-template-modal';

const COMPLEX_POLYMER_FIELDS_QUERY = gql`
  query ComplexPolymerFieldsQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymerSchemaConfiguration(schemaId: $schemaId) {
      ... on ComplexPolymerConfigInterface {
        id
        configValues {
          schemaField {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingSetNameTemplateModal(props: SetNameTemplateModalProps): JSX.Element {
  const result = useSkippableBenchlingQuery<ComplexPolymerFieldsQuery, ComplexPolymerFieldsQueryVariables>(
    COMPLEX_POLYMER_FIELDS_QUERY,
    {
      variables: {schemaId: props.schemaId!},
      skip:
        !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES ||
        props.schemaId == null ||
        APIUtil.isStudySchema(props.schemaId),
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (
    result.type === QueryResultType.ERROR ||
    result.data?.complexPolymerSchemaConfiguration == null ||
    isInaccessible(result.data.complexPolymerSchemaConfiguration)
  ) {
    assertAndContinue(result.type !== QueryResultType.ERROR, 'Error querying for complex polymer fields');
    return <SetNameTemplateModal {...props} complexPolymerSchemaFields={[]} />;
  }
  const complexPolymerFieldIds =
    result.data.complexPolymerSchemaConfiguration.configValues.map((c) => c.schemaField.id) ?? [];
  const complexPolymerFields = props.schemaFields.filter((f) =>
    complexPolymerFieldIds.includes(f.apiId ?? '')
  );
  assertAndContinue(
    complexPolymerFields.length === complexPolymerFieldIds.length,
    'Expected schema fields were not found'
  );

  return <SetNameTemplateModal {...props} complexPolymerSchemaFields={complexPolymerFields} />;
}

export class SetNameTemplateModal extends React.PureComponent<
  PresentationalSetNameTemplateModalProps,
  State
> {
  state: State = {
    nameTemplateParts: this.props.initialNameTemplateParts,
    shouldOrderNamePartsBySequence: this.props.initialShouldOrderNamePartsBySequence,
    draggingNameTemplatePartIndex: -1,
  };

  dropdown?: SelectDropdown | null;

  private handleChangeOrderSequenceParts = (e: InputChangeEvent): void => {
    this.setState({shouldOrderNamePartsBySequence: e.target.checked});
  };

  appendPart(part: NameTemplatePart): void {
    this.setState({nameTemplateParts: [...this.state.nameTemplateParts, part]});
  }

  private handleRemovePart = (index: number): void => {
    this.setState({nameTemplateParts: reactUpdate(this.state.nameTemplateParts, {$splice: [[index, 1]]})});
  };

  private handleDragStart = (index: number): void => {
    this.setState({draggingNameTemplatePartIndex: index});
  };

  private handleDrop = (index: number): void => {
    const {draggingNameTemplatePartIndex} = this.state;
    const nameTemplatePart = this.state.nameTemplateParts[draggingNameTemplatePartIndex];
    const reorderedNameTemplateParts = [...this.state.nameTemplateParts];
    reorderedNameTemplateParts.splice(draggingNameTemplatePartIndex, 1);
    reorderedNameTemplateParts.splice(index, 0, nameTemplatePart);
    this.setState({nameTemplateParts: reorderedNameTemplateParts});
  };

  private handleSelectItem = (callback: () => void): void => {
    if (this.dropdown) {
      closeSelectDropdown(this.dropdown);
    }
    callback();
  };

  private validateNameTemplateParts = (): string | null => {
    const {nameTemplateParts} = this.state;
    const nameTemplateWillProduceEmptyName =
      nameTemplateParts.length &&
      nameTemplateParts.every((nameTemplatePart) => nameTemplatePart.type === NameTemplatePartType.SEPARATOR);
    return nameTemplateWillProduceEmptyName
      ? 'This name template will produce an empty name because it only includes separators. Please include at least one non-separator component.'
      : null;
  };

  private handleSubmit = (): void => {
    const {onRequestSubmit, onRequestClose, initialNameTemplateParts, initialShouldOrderNamePartsBySequence} =
      this.props;
    const {nameTemplateParts, shouldOrderNamePartsBySequence} = this.state;
    const didNameTemplatePartsChange = !isEqual(initialNameTemplateParts, nameTemplateParts);
    const didShouldOrderNamePartsBySequenceChange =
      initialShouldOrderNamePartsBySequence !== shouldOrderNamePartsBySequence;
    if (!(didNameTemplatePartsChange || didShouldOrderNamePartsBySequenceChange)) {
      onRequestClose();
      return;
    }
    onRequestSubmit({nameTemplateParts, shouldOrderNamePartsBySequence});
  };

  render(): JSX.Element {
    const {bioentityType, sequenceType, onRequestClose} = this.props;
    const {nameTemplateParts, shouldOrderNamePartsBySequence} = this.state;
    const invalidNameTemplateError = this.validateNameTemplateParts();
    return (
      <Modal
        title={`Set ${nameTemplateTypeToFriendlyName(this.props.nameTemplateType, this.props.nameTemplatePartConfiguration)} name template`}
        onRequestClose={onRequestClose}
        onRequestSubmit={this.handleSubmit}
        buttons={[
          {
            text: 'Set',
            disabled: !nameTemplateParts.length || invalidNameTemplateError != null,
            submit: true,
          },
        ]}
      >
        <div className={classNames('u-small-spacing-bottom', css(nameTemplateModalStyles.helpText))}>
          You can re-order components by dragging them.
        </div>
        <div className={css(nameTemplateModalStyles.partChips)}>
          <DraggableNameTemplate
            draggable={true}
            handleDragStart={this.handleDragStart}
            handleDrop={this.handleDrop}
            nameTemplateParts={nameTemplateParts}
            schemaFields={[
              ...getNonArchivedOrComputedFields(this.props.schemaFields),
              ...this.props.complexPolymerSchemaFields,
            ]}
            onRemovePart={this.handleRemovePart}
            nameTemplatePartConfiguration={this.props.nameTemplatePartConfiguration}
          />
        </div>
        <div className="u-small-spacing-bottom">
          <SelectDropdown
            displayName="New component..."
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                onSelect={this.handleSelectItem}
                optionDatas={getSupportedNameTemplatePartOptions(
                  this.props.nameTemplateType,
                  this.props.schemaFields,
                  (part: NameTemplatePart) => {
                    this.appendPart(part);
                  },
                  this.props.complexPolymerSchemaFields,
                  this.props.nameTemplatePartConfiguration
                )}
              />
            }
            ref={(node) => {
              this.dropdown = node;
            }}
          />
        </div>
        {nameTemplateParts.length > 0 && (
          <div className={classNames('u-overflow-wrap-break-word', css(nameTemplateModalStyles.example))}>
            <span className={css(nameTemplateModalStyles.exampleLabel)}>Preview: </span>
            <span className={css(nameTemplateModalStyles.exampleContent)}>
              {makeExampleForNameTemplate(
                nameTemplateParts,
                [
                  ...getNonArchivedOrComputedFields(this.props.schemaFields),
                  ...this.props.complexPolymerSchemaFields,
                ],
                this.props.nameTemplatePartConfiguration
              )}
            </span>
          </div>
        )}
        {shouldOrderNamePartsBySequence !== undefined &&
          bioentityType === ModelTypeConstants.SEQUENCE &&
          sequenceType !== SequenceSchemaType.OLIGO && (
            <div className="u-small-spacing-top">
              <label>
                <input
                  type="checkbox"
                  onChange={this.handleChangeOrderSequenceParts}
                  checked={shouldOrderNamePartsBySequence}
                />
                If one or many part link fields are included in the name template, list parts in the order
                they appear on the sequence map, sorted by start position and then end position.
              </label>
            </div>
          )}
        {invalidNameTemplateError != null && (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={invalidNameTemplateError} />
        )}
      </Modal>
    );
  }
}
